import { Component, OnInit, input, model, output, inject } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ApContentService, ApKeyOverridePipe } from '@ap/content';
import { ApModalService, ModalClosedReason, ModalClosedResult, ModalOptions } from '@ap/modal';
import { ErrorMessages } from '@ap/form/errors';
import { ApContentPipe } from '@ap/content';
import { ApFormControlPipe } from '@ap/cdk';
import { ApAmountInputComponent } from '@ap/input/amount';

import {
    CalculatedIncome,
    EntrepreneurIncome,
    EntrepreneurIncomeForm,
    IncomeForm,
    IncomeModalOptions,
    IncomeTypeForm,
    MonthlyIncome,
    MonthlyIncomeForm
} from './income.model';
import { ApIncomeService } from './income.service';
import { ApCalculateIncomeComponent } from './calculate-income/calculate-income.component';
import content from './income.content.json';

@Component({
    selector: 'ap-income',
    templateUrl: './income.component.html',
    imports: [ApAmountInputComponent, FormsModule, ReactiveFormsModule, ApFormControlPipe, ApContentPipe, ApKeyOverridePipe],
    providers: [ApIncomeService]
})
export class ApIncomeComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly incomeService = inject(ApIncomeService);
    private readonly modalService = inject(ApModalService);

    /**
     * Required - IncomeForm FormGroup
     */
    public readonly incomeForm = input.required<FormGroup<IncomeForm>>();

    /**
     * MonthlyIncomeForm FormGroup.
     * If not passed an empty form will be generated
     */
    public readonly monthlyIncomeForm = model<FormGroup<MonthlyIncomeForm>>();

    /**
     * Boolean whether to display the entrepreneur income form
     * Default false
     */
    public readonly showEntrepreneurIncome = input(false);

    /**
     * EntrepreneurIncomeForm FormGroup.
     * If not passed an empty form will be generated
     */
    public readonly entrepreneurIncomeForm = model<FormGroup<EntrepreneurIncomeForm>>();

    public readonly incomeTypeForm = model<FormGroup<IncomeTypeForm>>();

    /**
     * Number of decimals used in component
     * Defaults to 2
     */
    public readonly numberOfDecimals = input(2);

    /**
     * Object to overwrite default content keys
     */
    public readonly contentKeyMap = input<{
        [contentKey: string]: string;
    }>(undefined);

    /**
     * Emits calculated income event on closing valid income model
     */
    public readonly calculateIncomeClosed = output<CalculatedIncome>();

    /**
     * Emits event on opening income model
     */
    public readonly calculateIncomeOpened = output<void>();

    protected yearlyIncomeErrorMessages: ErrorMessages;

    ngOnInit(): void {
        this.contentService.addContentItems(content);
        const contentKeyMap = this.contentKeyMap();
        if (contentKeyMap) {
            this.contentService.addContentItems(contentKeyMap as any);
        }
        if (!this.monthlyIncomeForm()) {
            this.monthlyIncomeForm.set(this.incomeService.createMonthlyIncomeForm());
        }
        if (!this.entrepreneurIncomeForm()) {
            this.entrepreneurIncomeForm.set(this.incomeService.createEntrepreneurIncomeForm());
        }
        if (!this.incomeTypeForm()) {
            this.incomeTypeForm.set(this.incomeService.createIncomeTypeForm());
        }
        this.setErrorMessages();
    }

    protected calculateIncomeFromMonthly(monthlyIncome: MonthlyIncome) {
        // the field is marked as touched and dirty to display potential error messages on a disabled field
        this.incomeForm().controls.yearlyIncome.markAsDirty();
        this.incomeForm().controls.yearlyIncome.markAsTouched();
        const calculatedIncome = this.incomeService.calculateAnnualIncome(monthlyIncome, this.numberOfDecimals());
        this.incomeForm().controls.yearlyIncome.patchValue(calculatedIncome);
    }

    protected calculateIncomeFromEntrepreneur(entrepreneurIncome: EntrepreneurIncome) {
        // the field is marked as touched and dirty to display potential error messages on a disabled field
        this.incomeForm().controls.yearlyIncome.markAsDirty();
        this.incomeForm().controls.yearlyIncome.markAsTouched();
        const calculatedIncome = this.incomeService.calculateAnnualIncomeEntrepreneur(entrepreneurIncome, this.numberOfDecimals());
        this.incomeForm().controls.yearlyIncome.patchValue(calculatedIncome);
    }

    protected openMonthlyIncomeModal() {
        const modalOptions: ModalOptions<IncomeModalOptions> = new ModalOptions();
        modalOptions.title = this.contentService.get(this.getKey('income.input.calculateYearlyIncome'));
        modalOptions.options = {
            incomeTypeForm: this.incomeTypeForm(),
            incomeForm: this.monthlyIncomeForm(),
            showEntrepreneurIncome: this.showEntrepreneurIncome(),
            entrepreneurIncomeForm: this.entrepreneurIncomeForm(),
            numberOfDecimals: this.numberOfDecimals(),
            contentKeyMap: this.contentKeyMap()
        };

        const modalContainer = this.modalService.open(ApCalculateIncomeComponent, modalOptions);
        // TODO: The 'emit' function requires a mandatory void argument
        this.calculateIncomeOpened.emit();
        modalContainer.afterClosed().subscribe((result: ModalClosedResult<CalculatedIncome>) => {
            if (result.reason === ModalClosedReason.ACCEPTED) {
                this.calculateIncomeClosed.emit(result.data);
                if (result.data.type === 'employment') {
                    this.calculateIncomeFromMonthly(result.data.monthlyIncome);
                }
                if (result.data.type === 'entrepreneur') {
                    this.calculateIncomeFromEntrepreneur(result.data.entrepreneurIncome);
                }
            }
        });
    }

    private setErrorMessages(): void {
        this.yearlyIncomeErrorMessages = {
            min: this.getKey('income.input.yearlyIncome.error.min'),
            max: this.getKey('income.input.yearlyIncome.error.max'),
            required: this.getKey('income.input.yearlyIncome.error.required')
        };
    }

    private getKey(initialKey: string): string {
        return new ApKeyOverridePipe().transform(initialKey, this.contentKeyMap());
    }
}
